/**
 * Global error toast handler.
 *
 * Listens for ft-fetch-error events from clientFetch and displays
 * user-friendly toast notifications.
 *
 * Precondition: ft-toast component registered and present in DOM
 * Postcondition: Error events trigger toast notifications
 * Invariant: One toast per error event
 */

import type { FetchError } from '../../shared/api-fetch.js';

const ERROR_MESSAGES: Record<FetchError['type'], string> = {
  timeout: 'Request timed out. Please try again.',
  rate_limit: 'Too many requests. Please wait a moment.',
  network: 'Network error. Check your connection.',
  http: 'Failed to load data.',
};

/**
 * Initialize global error toast listener.
 *
 * Precondition: Called once at app startup
 * Postcondition: Window listens for ft-fetch-error events
 */
export function initErrorToast(): void {
  window.addEventListener('ft-fetch-error', ((e: CustomEvent<FetchError>) => {
    const error = e.detail;
    let message = ERROR_MESSAGES[error.type] || 'Something went wrong.';

    // Add retry info for rate limits
    if (error.type === 'rate_limit' && error.retryAfter) {
      message = `Too many requests. Try again in ${error.retryAfter}s.`;
    }

    document.dispatchEvent(
      new CustomEvent('ft-toast', {
        detail: { message, type: 'error' },
      })
    );
  }) as EventListener);
}
